/**
 * Admin 共用 header(會員 / 訂單 / 帳號 三個 tab + 登出)
 *
 * 用法: <AdminHeader active="members" />
 * active: "members" | "orders" | "account"
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../store/authStore";


type AdminTab = "dashboard" | "members" | "orders" | "ship" | "shipments" | "forecast" | "supply" | "banquet" | "codes" | "products" | "traceability" | "brand-events" | "interactions" | "account" | "settings";

const TABS: { key: AdminTab; label: string; path: string }[] = [
  { key: "dashboard", label: "首頁", path: "/admin" },
  { key: "members", label: "會員管理", path: "/admin/members" },
  { key: "orders", label: "訂單管理", path: "/admin/orders" },
  { key: "ship", label: "出貨", path: "/admin/ship" },
  { key: "shipments", label: "出貨管理", path: "/admin/shipments" },
  { key: "forecast", label: "出貨預估", path: "/admin/forecast" },
  { key: "supply", label: "叫貨出貨", path: "/admin/supply" },
  { key: "banquet", label: "私宴預約", path: "/admin/banquet" },
  { key: "codes", label: "推薦碼", path: "/admin/codes" },
  { key: "products", label: "販售品項", path: "/admin/products" },
  { key: "traceability", label: "溯源管理", path: "/admin/traceability" },
  { key: "brand-events", label: "品牌活動", path: "/admin/brand-events" },
  { key: "interactions", label: "互動分析", path: "/admin/interactions" },
];


export default function AdminHeader({ active }: { active: AdminTab }) {
  const navigate = useNavigate();
  const email = useAuthStore((s) => s.email);
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header className="bg-primary text-white">
      <div className="px-6 py-4 flex items-center justify-between">
        <h1 className="text-lg font-bold">霽雲 · 後台</h1>
        <div className="relative">
          <button
            onClick={() => setMenuOpen((o) => !o)}
            className="flex items-center gap-2 text-sm opacity-90 hover:opacity-100"
            aria-label="帳號選單"
          >
            <span>{email || "—"}</span>
            <span className="text-xs">▾</span>
          </button>
          {menuOpen && (
            <>
              {/* click outside backdrop */}
              <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
              <div className="absolute right-0 mt-2 bg-white text-ink rounded-lg shadow-lg overflow-hidden z-20 min-w-44">
                <button
                  onClick={() => { setMenuOpen(false); navigate("/admin/account"); }}
                  className={`block w-full text-left px-4 py-3 text-sm hover:bg-cream ${active === "account" ? "bg-cream font-bold" : ""}`}
                >
                  變更密碼
                </button>
                <button
                  onClick={() => { setMenuOpen(false); navigate("/admin/settings"); }}
                  className={`block w-full text-left px-4 py-3 text-sm hover:bg-cream ${active === "settings" ? "bg-cream font-bold" : ""}`}
                >
                  系統設定
                </button>
                <div className="border-t border-line" />
                <button
                  onClick={() => {
                    useAuthStore.getState().logout();
                    navigate("/admin/login", { replace: true });
                  }}
                  className="block w-full text-left px-4 py-3 text-sm text-coral hover:bg-cream"
                >
                  登出
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      {/* Tab bar */}
      <nav className="px-6 flex gap-1 border-b border-white/20 overflow-x-auto">
        {TABS.map((t) => {
          const isActive = t.key === active;
          return (
            <button
              key={t.key}
              onClick={() => navigate(t.path)}
              className={`px-4 py-3 text-sm font-medium whitespace-nowrap shrink-0 border-b-2 transition ${
                isActive
                  ? "border-coral text-white"
                  : "border-transparent text-white/70 hover:text-white"
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </nav>
    </header>
  );
}
